import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../../api/contexto'
import type { ErrorApi } from '../../api/errores'
import type { Canal } from './consultas'

/** Etapas del pipeline (estilo Vocero). El orden es el del flujo comercial. */
export const ETAPAS = ['nuevo', 'en_conversacion', 'interesado', 'cliente', 'perdido'] as const
export type Etapa = (typeof ETAPAS)[number]

export const ETIQUETA_ETAPA: Record<Etapa, string> = {
  nuevo: 'Nuevo',
  en_conversacion: 'En conversación',
  interesado: 'Interesado',
  cliente: 'Cliente',
  perdido: 'Perdido',
}

/** Lo que el backend devuelve del crm.lead de Odoo. */
export interface LeadOdoo {
  id: number
  nombre: string
  contacto: string
  telefono: string
  email: string
  codigoPostal: string
  ciudad: string
  calle: string
  stageId: number | null
  stageNombre: string
  ingresoEsperado: number
}

/** Mapeo crm.stage de Odoo → etapa del panel, para pintar la etapa activa. */
export function etapaDesdeStage(stageId: number | null): Etapa | null {
  switch (stageId) {
    case 1:
      return 'nuevo'
    case 3:
      return 'interesado' // Proposition: "en_conversacion" también cae aquí; mostramos la más avanzada
    case 4:
      return 'cliente'
    case 6:
      return 'perdido'
    default:
      return null
  }
}

function ruta(canal: Canal, sufijo: string): string {
  return `/api/conversaciones/${canal}/lead/${sufijo}`
}

/** Ficha + etapa actuales del lead enlazado por teléfono. */
export function useLead(canal: Canal, telefono: string | null) {
  const api = useApi()
  return useQuery<{ lead: LeadOdoo | null }, ErrorApi>({
    queryKey: ['lead', canal, telefono],
    enabled: !!telefono,
    queryFn: ({ signal }) =>
      api.pedir(ruta(canal, encodeURIComponent(telefono as string)), { senal: signal }),
    staleTime: 30_000,
  })
}

export interface FichaInput {
  telefono: string
  contactName?: string
  email?: string
  zip?: string
  city?: string
  street?: string
  expectedRevenue?: number
}

type Respuesta = { odoo: boolean; leadId: number | null }

/** Guarda la ficha en Odoo (y crea el lead si no existe). */
export function useGuardarFicha(canal: Canal) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<Respuesta, ErrorApi, FichaInput>({
    mutationFn: (ficha) =>
      api.pedir(ruta(canal, 'ficha'), { metodo: 'POST', cuerpo: ficha }),
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ['lead', canal, v.telefono] }),
  })
}

/** Postea una nota libre en el chatter del lead. */
export function useGuardarNota(canal: Canal) {
  const api = useApi()
  return useMutation<Respuesta, ErrorApi, { telefono: string; nota: string }>({
    mutationFn: (v) => api.pedir(ruta(canal, 'nota'), { metodo: 'POST', cuerpo: v }),
  })
}

/** Mueve la etapa del pipeline (traducida a crm.stage de Odoo). */
export function useMoverEtapa(canal: Canal) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<Respuesta, ErrorApi, { telefono: string; etapa: Etapa }>({
    mutationFn: (v) => api.pedir(ruta(canal, 'etapa'), { metodo: 'POST', cuerpo: v }),
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ['lead', canal, v.telefono] }),
  })
}
